// packages/web/src/components/PosterImage.tsx — WS5: the reusable PROJECT
// POSTER treatment. Wikidata gives a project metadata but never poster art, so
// the poster is user-supplied: render the uploaded image (through the same
// object-URL MediaImage every other stored blob uses) when one exists, else the
// existing medium-tinted gradient placeholder. `PosterUpload` is the
// "Add / Change poster" affordance (multipart -> POST /api/actor/media
// kind=poster, project_id); newest poster wins on the server, so a re-upload
// just supersedes. General rule for any image slot: uploaded image OR tasteful
// placeholder + an upload affordance.
import { useState } from 'preact/hooks';
import { MediaImage } from './MediaEl';
import { phaseArtClass } from '../screens/projectPresentation';
import { uploadMedia, ApiError } from '../lib/api';
import './imageUpload.css';
import './PosterImage.css';

export interface PosterArtProps {
  tenantId: string;
  /** The current poster media id (newest kind='poster' for the project), or
   * null/undefined to fall back to the gradient placeholder. */
  mediaId?: string | null;
  /** actor_project.medium — picks the gradient tint for the placeholder. */
  medium: string | null;
  /** The year chip overlaid on the art (optional). */
  year?: string | null;
  alt: string;
  /** The hero uses the taller art shell (`.project-detail__art`). */
  tall?: boolean;
}

/** The poster art itself — an uploaded image when present, else the gradient.
 * Used identically in the Projects grid card and the ProjectDetail hero. */
export function PosterArt({ tenantId, mediaId, medium, year, alt, tall }: PosterArtProps) {
  const tallCls = tall ? ' project-detail__art' : '';
  if (mediaId) {
    return (
      <div class={`poster__art poster__art--img${tallCls}`}>
        <div class="poster__img-wrap">
          <MediaImage tenantId={tenantId} id={mediaId} alt={alt} className="poster__img" />
        </div>
        {year && <span class="poster__yr poster__yr--over">{year}</span>}
      </div>
    );
  }
  return (
    <div class={`poster__art ${phaseArtClass(medium)}${tallCls}`}>
      {year && <span class="poster__yr">{year}</span>}
    </div>
  );
}

export interface PosterUploadProps {
  tenantId: string;
  projectId: string;
  /** True when a poster already exists (labels the control "Change" vs "Add"). */
  hasPoster: boolean;
  /** Called after a successful upload so the caller can reload the poster. */
  onDone: () => void;
}

/** The calm "Add / Change poster" control — mirrors AddCallSheet. Uploads the
 * image via the media pipeline (kind=poster, project link) then asks the caller
 * to refresh. Image-only accept; never blocks the layout. */
export function PosterUpload({ tenantId, projectId, hasPoster, onDone }: PosterUploadProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadMedia(tenantId, 'poster', file, { projectId });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload the poster.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="poster-upload">
      <label class="img-upload__btn">
        <input
          type="file"
          accept="image/*"
          class="img-upload__input"
          onChange={(e) => void onPick(e)}
          disabled={busy}
          data-testid="poster-upload-input"
        />
        {busy ? 'Uploading…' : hasPoster ? 'Change poster' : 'Add poster'}
      </label>
      {error && <div class="img-upload__error">{error}</div>}
    </div>
  );
}
